import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Flex, Row, Skeleton, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EChart, type EChartClick, type EChartOption } from '../../components/EChart';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  fetchPayablesSummary,
  fetchReceivablesSummary,
  type Ledger,
  type LedgerItemsFilter,
  type LedgerParty,
} from './api';
import { unitsTooltipFormatter } from './chart-tooltip';
import { plotValue } from './figures';
import { LedgerItemsDrawer } from './LedgerItemsDrawer';
import { Tile } from './Tile';

const { Text } = Typography;

interface Drill {
  filter: LedgerItemsFilter;
  title: string;
}

/**
 * Insights, Receivables: what customers owe, as of AutoCount's latest read.
 * It has no year: the ageing is of today, so the page's year does not apply.
 */
export function LedgerTab({ ledger }: { ledger: Ledger }): ReactNode {
  const { t } = useTranslation();
  const copy = ledger === 'AR' ? 'receivables' : 'payables';
  const [drill, setDrill] = useState<Drill | null>(null);
  const summary = useQuery({
    queryKey: ['analytics', copy, 'summary'],
    queryFn: ledger === 'AR' ? fetchReceivablesSummary : fetchPayablesSummary,
  });

  const option = useMemo((): EChartOption => {
    if (summary.data === undefined) return {};
    const { buckets } = summary.data;
    const axisText = { color: 'var(--ink-muted)' };
    const label = (bucket: string): string => t(`insights.${copy}.bucket.${bucket}`);
    return {
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: unitsTooltipFormatter(
          (_id, index) => {
            const b = buckets[index];
            return b === undefined ? undefined : formatMoney(b.amount);
          },
          (index) => label(buckets[index]?.bucket ?? ''),
        ),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: {
        type: 'category',
        data: buckets.map((b) => label(b.bucket)),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      yAxis: { type: 'value', axisLabel: axisText, splitLine: { lineStyle: { color: 'var(--line)' } } },
      series: [
        {
          id: 'OWED',
          name: t(`insights.${copy}.owed`),
          type: 'bar',
          barMaxWidth: 48,
          cursor: 'pointer',
          itemStyle: { color: 'var(--series-1)' },
          emphasis: { focus: 'series' },
          data: buckets.map((b) => plotValue(b.amount)),
        },
      ],
    };
  }, [summary.data, t]);

  if (summary.error !== null) {
    const denied = summary.error instanceof ApiError && summary.error.status === 403;
    return <Alert type="error" showIcon message={t(denied ? 'insights.noAccess' : 'insights.loadFailed')} />;
  }
  if (summary.data === undefined) return <Skeleton active paragraph={{ rows: 8 }} />;

  const data = summary.data;
  if (data.dataAsOf === null) {
    return <Alert type="info" showIcon message={t(`insights.${copy}.nothingYet`)} />;
  }

  const bucketLabel = (bucket: string): string => t(`insights.${copy}.bucket.${bucket}`);
  const over90 = data.buckets.find((b) => b.bucket === 'D90_PLUS');

  const openBucket = (click: EChartClick): void => {
    const b = data.buckets[click.dataIndex];
    if (b === undefined) return;
    setDrill({
      filter: { kind: 'OPEN', bucket: b.bucket },
      title: t(`insights.${copy}.bucketTitle`, { bucket: bucketLabel(b.bucket) }),
    });
  };

  const openParty = (d: LedgerParty): void => {
    setDrill({
      filter: { kind: 'OPEN', partyCode: d.code },
      title: t(`insights.${copy}.partyTitle`, { party: d.name ?? d.code }),
    });
  };

  const openCreditNotes = (): void => {
    setDrill({ filter: { kind: 'CREDIT_NOTES' }, title: t(`insights.${copy}.creditNotesTitle`) });
  };

  const openUnapplied = (): void => {
    setDrill({ filter: { kind: 'UNAPPLIED' }, title: t(`insights.${copy}.unappliedTitle`) });
  };

  const columns: TableProps<LedgerParty>['columns'] = [
    {
      title: t(`insights.${copy}.party`),
      key: 'party',
      render: (_: unknown, d) => (
        <Flex vertical>
          <span>{orDash(d.name)}</span>
          <Text type="secondary" className="tabular">
            {d.code}
          </Text>
        </Flex>
      ),
    },
    {
      title: t(`insights.${copy}.owed`),
      key: 'owed',
      align: 'right',
      render: (_: unknown, d) => (
        <Button
          type="link"
          className="tabular"
          style={{ padding: 0 }}
          onClick={() => {
            openParty(d);
          }}
        >
          {formatMoney(d.owed)}
        </Button>
      ),
    },
    {
      title: t(`insights.${copy}.over90`),
      key: 'over90',
      align: 'right',
      render: (_: unknown, d) => formatMoney(d.over90),
    },
    {
      title: t(`insights.${copy}.oldest`),
      key: 'oldest',
      align: 'right',
      render: (_: unknown, d) => (d.oldestDaysPastDue > 0 ? d.oldestDaysPastDue : '—'),
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Flex vertical>
        <Text type="secondary">{t('insights.asRead', { at: formatDateTime(data.dataAsOf) })}</Text>
        <Text type="secondary">{t(`insights.${copy}.notByYear`)}</Text>
      </Flex>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={6}>
          <Tile id="owed" label={t(`insights.${copy}.owed`)} value={formatMoney(data.owed)}>
            <Text type="secondary">
              {t(`insights.${copy}.owedHelp`, { items: data.openItems, parties: data.parties })}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile
            id="over90"
            label={t(`insights.${copy}.over90`)}
            value={formatMoney(over90?.amount ?? '0.00')}
          >
            <Text type="secondary">
              {t(`insights.${copy}.over90Help`, { items: over90?.items ?? 0 })}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile
            id="unapplied"
            label={t(`insights.${copy}.unapplied`)}
            value={formatMoney(data.unapplied.amount)}
          >
            <Text type="secondary">
              {t(`insights.${copy}.unappliedCount`, { count: data.unapplied.count })}
            </Text>
            <Text type="secondary">{t(`insights.${copy}.unappliedApart`)}</Text>
            {data.unapplied.count > 0 && (
              <Button type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={openUnapplied}>
                {t(`insights.${copy}.seeUnapplied`)}
              </Button>
            )}
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Tile
            id="creditNotes"
            label={t(`insights.${copy}.creditNotes`)}
            value={formatMoney(data.creditNotes.amount)}
          >
            <Text type="secondary">
              {t(`insights.${copy}.creditNotesCount`, { count: data.creditNotes.count })}
            </Text>
            <Text type="secondary">{t(`insights.${copy}.unappliedApart`)}</Text>
            {data.creditNotes.count > 0 && (
              <Button type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={openCreditNotes}>
                {t(`insights.${copy}.seeCreditNotes`)}
              </Button>
            )}
          </Tile>
        </Col>
      </Row>

      <Card size="small" title={t(`insights.${copy}.chartTitle`)}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t(`insights.${copy}.chartHelp`)}</Text>
          <EChart option={option} height={300} onClick={openBucket} />
        </Flex>
      </Card>

      <Card size="small" title={t(`insights.${copy}.topTitle`)} data-testid="top-parties">
        <Flex vertical gap={8}>
          <Text type="secondary">{t(`insights.${copy}.topHelp`)}</Text>
          <Table<LedgerParty>
            rowKey="code"
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t(`insights.${copy}.noParties`) }}
            dataSource={data.topParties}
            columns={columns}
          />
        </Flex>
      </Card>

      <LedgerItemsDrawer
        ledger={ledger}
        filter={drill?.filter ?? null}
        title={drill?.title ?? ''}
        onClose={() => {
          setDrill(null);
        }}
      />
    </Flex>
  );
}
